import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import type {
  StepProps,
  WizardData,
  WeddingCustomData,
  WeddingEventItem,
  WeddingMediaItem,
  WeddingPersonDetails,
  WeddingFamilyCouple,
  BirthdayCustomData,
  BirthdayCelebrant,
  BirthdaySnapshot,
  BirthdayActivity,
  BirthdayTimelineItem,
  BirthdayFunFact,
  BirthdayStory,
  BirthdaySectionBackground,
} from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { MediaUploadField } from '@/src/modules/components/MediaUploadField';
import { PLOT_PLANE_TEMPLATE_ID, PlotPlanePhotoSlots, writePlotPlanePhoto } from './PlotPlanePhotoSlots';
import { LiquidToggle } from '@/src/modules/components/ui/liquid-toggle';
import { removeImageBackground } from '@/src/infrastructure/lib/backgroundRemoval';
import { todayInputValue, isPastDateInput } from '@/src/infrastructure/lib/date';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import type { ApiTemplate, ApiInvitation } from '@/src/types';

type PanelId =
  | 'overview'
  | 'groom'
  | 'bride'
  | 'events'
  | 'family'
  | 'story'
  | 'invitations'
  | 'media'
  | 'rsvp'
  | 'plot-plane'
  | 'sacred-verse'
  | 'couple-bg'
  | 'organizers'
  | 'family-children'
  | 'blessings'
  | 'birthday-person'
  | 'birthday-celebrants'
  | 'birthday-gallery'
  | 'birthday-schedule'
  | 'birthday-timeline'
  | 'birthday-facts'
  | 'birthday-venue'
  | 'birthday-story'
  | 'birthday-capsule'
  | 'nikah-details'
  | 'anniversary-details'
  | 'baby-shower-details'
  | 'send-off-details'
  | 'birthday-neon-details';

type ValidationIssue = {
  panel: PanelId;
  field: string;
  message: string;
};

const PAST_DATE_INPUT_MESSAGE = 'Please select today or a future date.';

const inputClass =
  'w-full rounded-2xl border border-outline-variant bg-surface px-4 py-3.5 outline-none transition-all placeholder:text-on-surface-variant/30 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:[color-scheme:dark]';
const labelClass = 'ml-1 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant';

function panelSummary(id: PanelId) {
  switch (id) {
    case 'overview':
      return 'Core title, host, date, and welcome note.';
    case 'groom':
      return 'Name, parents, address, photo, and story.';
    case 'bride':
      return 'Name, parents, address, photo, and story.';
    case 'events':
      return 'Haldi, Wedding, and Reception venue timelines.';
    case 'family':
      return 'Groom and bride family trees — toggle each side on or off.';
    case 'story':
      return 'Pre-wedding shoot moments and descriptions — toggle on to add.';
    case 'invitations':
      return 'Family invitation cards shown in the "With Love From Our Family" section.';
    case 'media':
      return 'Cinematic gallery images and videos.';
    case 'rsvp':
      return 'Where the event happens — required. Plus the RSVP link.';
    case 'plot-plane':
      return 'One photo for each ceremony scene. Empty slots keep the sample plates.';
    case 'sacred-verse':
      return 'Appearance and welcome message of the opening sacred verse.';
    case 'couple-bg':
      return 'Custom background styles for the bride & groom sections.';
    case 'organizers':
      return 'Custom wedding hosts/organizers details.';
    case 'family-children':
      return 'Young children participating in the wedding celebration.';
    case 'blessings':
      return 'Settings and moderation for the guest blessings wall.';
    case 'birthday-person':
      return 'Birthday person name, age, tagline, and hero background photo.';
    case 'birthday-celebrants':
      return 'Family members and friends celebrating — name, relation, photo.';
    case 'birthday-gallery':
      return 'Snapshot photos with captions (supports up to 12 images).';
    case 'birthday-schedule':
      return 'Party timeline — time, activity title, and description.';
    case 'birthday-timeline':
      return 'Life milestones with photos — First Steps, First Birthday, etc.';
    case 'birthday-facts':
      return 'Fun facts about the birthday person — favourite food, colour, etc.';
    case 'birthday-venue':
      return 'Venue photo, address, Google Maps link, and parking info.';
    case 'birthday-story':
      return 'A personal story about the birthday person — title, content, and an optional quote.';
    case 'birthday-capsule':
      return 'Let guests leave a video/photo memory capsule message for the birthday person.';
    case 'nikah-details':
      return 'Muslim Nikah ceremony details: Bismillah calligraphy, welcome message, Qazi, and Mehr.';
    case 'anniversary-details':
      return 'Anniversary details: couple photos, years together, and customized messages.';
    case 'baby-shower-details':
      return 'Baby shower details: baby name, expecting parents, guest predictions, and baby photo.';
    case 'send-off-details':
      return 'Send-off details: person name, departure destination, wishes wall, and photo.';
    case 'birthday-neon-details':
      return 'Neon Bash details: birthday star name, age, title, and greeting description.';
  }
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  error,
  min,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  error?: string;
  min?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className={cn(labelClass, error && 'text-status-failed')} htmlFor={id}>
        {label}{required && <span className="ml-1 text-status-failed">*</span>}
      </label>
      <input
        id={id}
        className={cn(
          inputClass,
          error && 'border-status-failed bg-status-failed/5 ring-4 ring-status-failed/10 focus:border-status-failed focus:ring-status-failed/15',
        )}
        type={type}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        min={min}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && <p id={`${id}-error`} className="ml-1 text-xs font-semibold text-status-failed">{error}</p>}
    </div>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass} htmlFor={id}>{label}</label>
      <textarea
        id={id}
        className={cn(inputClass, 'resize-none leading-7')}
        rows={rows}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function EditorPanel({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: PanelId;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  key?: React.Key;
}) {
  return (
    <section className="overflow-hidden rounded-3xl border border-outline-variant/25 bg-surface-lowest shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-5 p-5 text-left sm:p-6"
      >
        <span>
          <span className="block text-lg font-bold text-on-surface">{title}</span>
          <span className="mt-1 block text-sm text-on-surface-variant">{panelSummary(id)}</span>
        </span>
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-surface-low text-primary"
        >
          <Icons.ChevronRight size={20} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-outline-variant/15 p-5 sm:p-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

/**
 * Renders the user's currently selected template with their live form data.
 * Works for code-rendered templates (registry), JSON-schema templates, and
 * renders live SceneExperienceRenderer manifests for experience templates.
 */
function LivePreview({
  templateId,
  previewData,
  template,
}: {
  templateId: string;
  previewData: ReturnType<typeof Object.assign>;
  template?: ApiTemplate | null;
}) {
  if (registry.has(templateId)) {
    return <TemplateRenderer templateId={templateId} data={previewData} />;
  }

  const apiInvitationData: ApiInvitation = {
    id: `live-preview-${templateId}`,
    publicSlug: null,
    templateId,
    occasionType: previewData.occasionType || 'BIRTHDAY',
    status: 'DRAFT',
    hostedUrl: null,
    eventName: previewData.eventName || 'Your Event Name',
    eventDate: previewData.eventDate || new Date().toISOString(),
    eventVenue: previewData.eventVenue || 'The Grand Venue',
    hostName: previewData.hostName || 'Your Name',
    customMessage: previewData.customMessage,
    rsvpLink: previewData.rsvpLink,
    mediaUrls: previewData.mediaUrls || [],
    customData: previewData.customData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  if (template?.renderMode === 'json-renderer' && template?.layoutSchema) {
    return (
      <JsonTemplateRenderer
        schema={template.layoutSchema}
        data={apiInvitationData}
        theme={template.themeConfig ?? undefined}
        mode="preview"
      />
    );
  }

  // preview: this renders inside a fixed-height, internally-scrolling panel
  // (h-[70vh] overflow-y-auto) alongside the form above it — not a full page.
  // Without it, SceneExperienceRenderer's OpeningSequence locks
  // document.body.style.overflow and Lenis takes over the *page's* native
  // scroll, breaking the form and the rest of the wizard page around this panel.
  return (
    <SceneExperienceRenderer
      manifest={resolveExperienceManifest(templateId, apiInvitationData)}
      preview
    />
  );
}

function emptyEvent(title = ''): WeddingEventItem {
  return {
    title,
    date: '',
    startTime: '',
    endTime: '',
    venue: '',
    mapsLink: '',
    description: '',
    coverImageUrl: '',
  };
}

function defaultWeddingEvents(): WeddingEventItem[] {
  return [emptyEvent('Haldi'), emptyEvent('Main Event'), emptyEvent('Reception')];
}

export function guessGenderByName(name: string): 'boy' | 'girl' {
  const n = (name || '').trim().toLowerCase();
  if (!n) return 'boy'; // default
  
  // Common girl name suffixes in Indian and general names:
  // e.g. names ending in 'a' (Kiara, Anaya, Pooja, Priya), 'i' (Aarti, Shruti), 'e' (Grace, Annie)
  const girlEndings = ['a', 'i', 'e', 'ee', 'ya', 'ha', 'ma', 'ta', 'na', 'ra', 'sha'];
  const girlKeywords = ['kiara', 'anaya', 'priya', 'rhea', 'isha', 'dia', 'ria', 'siya', 'tanya', 'navya', 'kavya', 'sanya', 'anya'];
  
  if (girlKeywords.some(kw => n.includes(kw))) return 'girl';
  
  const lastChar = n.charAt(n.length - 1);
  const lastTwo = n.slice(-2);
  
  if (girlEndings.includes(lastChar) || girlEndings.includes(lastTwo)) {
    // some exceptions for boys names ending in 'a' (e.g. Krishna, Rama, Shiva)
    const boyExceptions = ['shiva', 'krishna', 'rama', 'rishi', 'hari', 'adi'];
    if (boyExceptions.includes(n)) {
      return 'boy';
    }
    return 'girl';
  }
  
  return 'boy';
}

function emptyFamilyCouple(): WeddingFamilyCouple {
  return {
    id: `couple-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'couple',
    mrsName: '',
    mrName: '',
    singleName: '',
    label: '',
  };
}

function emptyMedia(type: WeddingMediaItem['type'] = 'image'): WeddingMediaItem {
  return { type, url: '', caption: '', description: '' };
}

function emptyInvitationCard(): import('@/src/types').FamilyInvitationCard {
  return {
    id: `inv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    relation: '',
    message: '',
  };
}

function emptyOrganizer(): import('@/src/types').Organizer {
  return {
    id: `org-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    photo: '',
    designation: '',
  };
}

function emptyFamilyChild(): import('@/src/types').FamilyChild {
  return {
    id: `child-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    photo: '',
  };
}

function defaultOrganizers(): import('@/src/types').Organizer[] {
  return [
    { id: 'org-1', name: 'Mr. Rajesh Sharma', designation: 'Main Organizer' },
    { id: 'org-2', name: 'Mrs. Sunita Sharma', designation: 'Co-Organizer' },
    { id: 'org-3', name: 'Mr. Amit Verma', designation: 'Coordinator' },
  ];
}

function defaultFamilyChildren(): import('@/src/types').FamilyChild[] {
  return [
    { id: 'child-1', name: 'Aryan' },
    { id: 'child-2', name: 'Kiara' },
    { id: 'child-3', name: 'Aarav' },
    { id: 'child-4', name: 'Anaya' },
  ];
}

/**
 * Reusable editor block for per-section background images.
 * Rendered inside each birthday editor panel. All state lives in the
 * parent via `bg` / `onBgChange` so no local state is needed here.
 */
function BgImageControls({
  sectionId,
  bg,
  onBgChange,
  draftId,
  saveDraftNow,
}: {
  sectionId: string;
  bg?: BirthdaySectionBackground;
  onBgChange: (patch: Partial<BirthdaySectionBackground>) => void;
  draftId?: string | null;
  saveDraftNow?: () => Promise<string | null>;
}) {
  return (
    <div className="border-t border-outline-variant/15 pt-5 mt-2 space-y-4">
      <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
        <LiquidToggle
          id={`${sectionId}-bg-toggle`}
          label="Enable Background Image"
          description="Add a photo behind this section"
          checked={Boolean(bg?.enabled)}
          onChange={(v) => onBgChange({ enabled: v })}
        />
      </div>
      {bg?.enabled && (
        <>
          <MediaUploadField
            id={`${sectionId}-bg-img`}
            label="Background Image"
            accept="image"
            value={bg.imageUrl}
            onChange={(imageUrl) => onBgChange({ imageUrl })}
            invitationId={draftId}
            ensureInvitationId={saveDraftNow}
          />
          <div className="flex flex-col gap-2">
            <span className={labelClass}>Overlay Style</span>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-on-surface select-none">
                <input
                  type="radio"
                  name={`${sectionId}-overlay`}
                  value="dark"
                  checked={(bg.overlayStyle ?? 'dark') === 'dark'}
                  onChange={() => onBgChange({ overlayStyle: 'dark' })}
                  className="accent-primary"
                />
                Dark (default)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-on-surface select-none">
                <input
                  type="radio"
                  name={`${sectionId}-overlay`}
                  value="light"
                  checked={bg.overlayStyle === 'light'}
                  onChange={() => onBgChange({ overlayStyle: 'light' })}
                  className="accent-primary"
                />
                Light
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function StepDetails({ onNext, onBack, data, updateData, draftId, saveDraftNow }: StepProps) {
  const wedding: WeddingCustomData = data.customData?.wedding ?? {};
  const isBabyShower = data.occasionType === 'BABY_SHOWER' || data.templateId === 'tpl_baby_shower_luxury_teddy_006';
  const isWedding = (data.occasionType === 'WEDDING' || data.occasionType === 'ENGAGEMENT') && !isBabyShower;
  const [openPanels, setOpenPanels] = useState<PanelId[]>(() =>
    data.templateId === PLOT_PLANE_TEMPLATE_ID
      ? ['overview', 'rsvp', 'plot-plane']
      : isWedding
        ? ['overview', 'groom', 'bride', 'events', 'rsvp']
        : ['overview', 'rsvp'],
  );
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const groomFamilyCouples: WeddingFamilyCouple[] = wedding.groomFamilyCouples ?? [];
  const brideFamilyCouples: WeddingFamilyCouple[] = wedding.brideFamilyCouples ?? [];
  const showGroomFamilyTree = wedding.showGroomFamilyTree !== false;
  const showBrideFamilyTree = wedding.showBrideFamilyTree !== false;
  // Gate the rich birthday editor on the NEW template id, not the occasion type.
  // The frozen `birthday-celebration` template keeps the default editor so its
  // existing invitations are never affected. Only `birthday-premium` opts in.
  const isBirthday = data.templateId === 'birthday-premium';
  const birthday: BirthdayCustomData = data.customData?.birthday ?? {};
  const isTraditionalWeddingInteractive = data.templateId === 'traditional-wedding-interactive';
  const isRoyalGanesh = data.templateId === 'royal-ganesh-interactive';

  const anniversary = data.customData?.anniversary ?? {};
  const baby = data.customData?.baby ?? {};
  const sendoff = data.customData?.sendoff ?? {};
  const birthdayNeon = data.customData?.birthday ?? {};

  const togglePanel = (id: PanelId) => {
    setOpenPanels((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  };

  const updateWedding = (patch: Partial<WeddingCustomData>) => {
    updateData((prev) => ({
      customData: {
        ...(prev.customData ?? {}),
        wedding: {
          ...(prev.customData?.wedding ?? {}),
          ...patch,
        },
      },
    }));
  };

  const updateBirthday = (patch: Partial<BirthdayCustomData>) => {
    updateData({
      customData: {
        ...(data.customData ?? {}),
        birthday: {
          ...(data.customData?.birthday ?? {}),
          ...patch,
        },
      },
    });
  };

  const updateAnniversary = (patch: Partial<any>) => {
    updateData({
      customData: {
        ...(data.customData ?? {}),
        anniversary: {
          ...(data.customData?.anniversary ?? {}),
          ...patch,
        },
      },
    });
  };

  const updateBaby = (patch: Partial<any>) => {
    updateData({
      customData: {
        ...(data.customData ?? {}),
        baby: {
          ...(data.customData?.baby ?? {}),
          ...patch,
        },
      },
    });
  };

  const updateSendoff = (patch: Partial<any>) => {
    updateData({
      customData: {
        ...(data.customData ?? {}),
        sendoff: {
          ...(data.customData?.sendoff ?? {}),
          ...patch,
        },
      },
    });
  };

  const updateBirthdayNeon = (patch: Partial<any>) => {
    updateData({
      customData: {
        ...(data.customData ?? {}),
        birthday: {
          ...(data.customData?.birthday ?? {}),
          ...patch,
        },
      },
    });
  };

  const updateSectionBg = (
    key: keyof NonNullable<BirthdayCustomData['sectionBackgrounds']>,
    patch: Partial<BirthdaySectionBackground>,
  ) => {
    updateBirthday({
      sectionBackgrounds: {
        ...birthday.sectionBackgrounds,
        [key]: { ...(birthday.sectionBackgrounds?.[key] ?? {}), ...patch },
      },
    });
  };

  const updatePerson = (side: 'groom' | 'bride', patch: Partial<WeddingPersonDetails>) => {
    updateWedding({ [side]: { ...(wedding[side] ?? {}), ...patch } });
  };

  const updateEvents = (events: WeddingEventItem[]) => updateWedding({ weddingEvents: events });
  const updateGroomFamilyCouples = (couples: WeddingFamilyCouple[]) => updateWedding({ groomFamilyCouples: couples });
  const updateBrideFamilyCouples = (couples: WeddingFamilyCouple[]) => updateWedding({ brideFamilyCouples: couples });
  const updatePreWeddingMoments = (moments: WeddingMediaItem[]) => {
    updateWedding({ preWeddingStory: { ...(wedding.preWeddingStory ?? {}), moments } });
  };
  const updateFamilyInvitations = (cards: import('@/src/types').FamilyInvitationCard[]) => {
    updateWedding({ familyInvitations: cards });
  };
  const updateOrganizers = (orgs: import('@/src/types').Organizer[]) => {
    updateWedding({ organizers: orgs });
  };
  const updateFamilyChildren = (children: import('@/src/types').FamilyChild[]) => {
    updateWedding({ familyChildren: children });
  };
  const updateMediaShowcase = (items: WeddingMediaItem[]) => {
    updateWedding({ mediaShowcase: items });
    updateData({ mediaUrls: items.filter((item) => item.type === 'image' && item.url).map((item) => item.url) });
  };

  const hasValue = (value: string | undefined) => Boolean(value?.trim());
  const fieldError = (field: string) => validationIssues.find((issue) => issue.field === field)?.message;
  const clearFieldIssue = (field: string) => {
    if (validationIssues.length === 0) return;
    setValidationIssues((prev) => prev.filter((issue) => issue.field !== field));
  };
  const setFieldIssue = (issue: ValidationIssue) => {
    setValidationIssues((prev) => [...prev.filter((item) => item.field !== issue.field), issue]);
  };
  const validateDateInputChange = (value: string, issue: ValidationIssue) => {
    if (isPastDateInput(value)) {
      setFieldIssue(issue);
      return;
    }
    clearFieldIssue(issue.field);
  };

  const validateDetails = (): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];

    if (!isBabyShower) {
      if (!hasValue(data.eventName)) issues.push({ panel: 'overview', field: 'eventName', message: 'Event Name is required.' });
      if (!hasValue(data.hostName)) issues.push({ panel: 'overview', field: 'hostName', message: 'Hosted By is required.' });
      if (!hasValue(data._dateInput)) issues.push({ panel: 'overview', field: '_dateInput', message: 'Primary Date is required.' });
      else if (isPastDateInput(data._dateInput)) issues.push({ panel: 'overview', field: '_dateInput', message: PAST_DATE_INPUT_MESSAGE });
      if (!hasValue(data._timeInput)) issues.push({ panel: 'overview', field: '_timeInput', message: 'Primary Start Time is required.' });
      if (!hasValue(data.eventVenue)) issues.push({ panel: 'rsvp', field: 'eventVenue', message: 'Venue Name & Address is required.' });
    } else {
      if (!hasValue(baby.motherName)) issues.push({ panel: 'baby-shower-details', field: 'baby-motherName', message: "Mother's Name is required." });
      if (!hasValue(baby.hostedBy)) issues.push({ panel: 'baby-shower-details', field: 'baby-hostedBy', message: 'Hosted By is required.' });
      if (!hasValue(baby.eventDate)) issues.push({ panel: 'baby-shower-details', field: 'baby-eventDate', message: 'Event Date is required.' });
      else if (isPastDateInput(baby.eventDate)) issues.push({ panel: 'baby-shower-details', field: 'baby-eventDate', message: PAST_DATE_INPUT_MESSAGE });
      if (!hasValue(baby.eventTime)) issues.push({ panel: 'baby-shower-details', field: 'baby-eventTime', message: 'Event Time is required.' });
      if (!hasValue(baby.venueName)) issues.push({ panel: 'baby-shower-details', field: 'baby-venueName', message: 'Venue Name is required.' });
    }

    if (isWedding) {
      events.forEach((event, index) => {
        const hasEventContent = [event.date, event.startTime, event.endTime, event.venue, event.description, event.coverImageUrl]
          .some(hasValue);
        if (!hasEventContent) return;
        if (!hasValue(event.title)) issues.push({ panel: 'events', field: `event-title-${index}`, message: `Event ${index + 1}: Title is required.` });
        if (!hasValue(event.date)) issues.push({ panel: 'events', field: `event-date-${index}`, message: `Event ${index + 1}: Date is required.` });
        else if (isPastDateInput(event.date)) issues.push({ panel: 'events', field: `event-date-${index}`, message: `Event ${index + 1}: ${PAST_DATE_INPUT_MESSAGE}` });
        if (!hasValue(event.venue)) issues.push({ panel: 'events', field: `event-venue-${index}`, message: `Event ${index + 1}: Venue is required.` });
      });

      if (showGroomFamilyTree) {
        groomFamilyCouples.forEach((c, i) => {
          if (c.type === 'single') {
            if (!hasValue(c.singleName)) {
              issues.push({ panel: 'family', field: `groom-couple-${i}`, message: `Groom's Family Member ${i + 1}: Name is required.` });
            }
          } else {
            if (!hasValue(c.mrsName) && !hasValue(c.mrName)) {
              issues.push({ panel: 'family', field: `groom-couple-${i}`, message: `Groom's Family Couple ${i + 1}: At least one name is required.` });
            }
          }
        });
      }

      if (showBrideFamilyTree) {
        brideFamilyCouples.forEach((c, i) => {
          if (c.type === 'single') {
            if (!hasValue(c.singleName)) {
              issues.push({ panel: 'family', field: `bride-couple-${i}`, message: `Bride's Family Member ${i + 1}: Name is required.` });
            }
          } else {
            if (!hasValue(c.mrsName) && !hasValue(c.mrName)) {
              issues.push({ panel: 'family', field: `bride-couple-${i}`, message: `Bride's Family Couple ${i + 1}: At least one name is required.` });
            }
          }
        });
      }
    }

    return issues;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const issues = validateDetails();
    setValidationIssues(issues);
    if (issues.length > 0) {
      const panelsWithIssues = issues.map((issue) => issue.panel);
      setOpenPanels((prev) => Array.from(new Set([...prev, ...panelsWithIssues])));
      window.requestAnimationFrame(() => {
        document.getElementById('details-validation-summary')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    const dateStr = isBabyShower ? baby.eventDate : data._dateInput;
    const timeStr = (isBabyShower ? baby.eventTime : data._timeInput) ?? '00:00';
    
    const updates: Partial<WizardData> = {};
    if (dateStr) {
      updates.eventDate = new Date(`${dateStr}T${timeStr}`).toISOString();
    }
    if (isBabyShower) {
      updates.eventName = baby.motherName ? `${baby.motherName}'s Baby Shower` : 'Baby Shower';
      updates.hostName = baby.hostedBy || 'Family';
      updates.eventVenue = baby.venueName || 'TBD';
      updates._dateInput = dateStr;
      updates._timeInput = timeStr;
    }
    
    if (Object.keys(updates).length > 0) {
      updateData(updates);
    }
    
    onNext();
  };

  const events = wedding.weddingEvents?.length ? wedding.weddingEvents : defaultWeddingEvents();
  const showPreWeddingStory = wedding.showPreWeddingStory === true;
  const showMediaShowcase = wedding.showMediaShowcase === true;
  const showWeddingSchedule = wedding.showWeddingSchedule !== false;
  const preWeddingMoments = wedding.preWeddingStory?.moments?.length ? wedding.preWeddingStory.moments : [emptyMedia('image')];
  const mediaShowcase = wedding.mediaShowcase?.length ? wedding.mediaShowcase : [emptyMedia('image')];
  const familyInvitations = wedding.familyInvitations ?? [];
  const showFamilyInvitations = wedding.showFamilyInvitations !== false;
  const organizers: import('@/src/types').Organizer[] = wedding.organizers?.length ? wedding.organizers : defaultOrganizers();
  const showOrganizers = wedding.showOrganizers === true;
  const familyChildren: import('@/src/types').FamilyChild[] = wedding.familyChildren?.length ? wedding.familyChildren : defaultFamilyChildren();
  const showFamilyChildren = wedding.showFamilyChildren === true;
  const familyChildrenTitle = wedding.familyChildrenTitle ?? 'Our Little Stars';
  const allowBlessings = wedding.allowBlessings !== false;
  const requireApproval = wedding.requireApproval === true;
  const allowPhotos = wedding.allowPhotos !== false;
  const allowReactions = wedding.allowReactions !== false;
  const blessingsTitle = wedding.blessingsTitle ?? 'Blessings For The Couple';
  const enableMockBlessings = wedding.enableMockBlessings === true;
  const mockBlessingsCount = wedding.mockBlessingsCount ?? 50;
  const blessingsLoadMode = wedding.blessingsLoadMode ?? 'loadMore';
  const previewData = {
    eventName: data.eventName || '',  // empty → template uses its own name defaults (e.g. Lagna Patrika → "Isha & Rohan")
    eventDate: data.eventDate || (data._dateInput ? new Date(`${data._dateInput}T${data._timeInput || '00:00'}`).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()),
    eventVenue: data.eventVenue || 'The Grand Venue, Mumbai',
    hostName: data.hostName || 'The Families',
    customMessage: data.customMessage,
    rsvpLink: data.rsvpLink || '#',
    mediaUrls: data.mediaUrls,
    customData: {
      ...data.customData,
      wedding: {
        ...(data.customData?.wedding ?? {}),
        weddingEvents: events,
        preWeddingStory: {
          ...(data.customData?.wedding?.preWeddingStory ?? {}),
          moments: preWeddingMoments,
        },
        mediaShowcase,
        organizers,
        familyChildren,
        allowBlessings,
        requireApproval,
        allowPhotos,
        allowReactions,
        blessingsTitle,
        enableMockBlessings,
        mockBlessingsCount,
        blessingsLoadMode,
        showFamilyInvitations,
      },
    },
  };

  return (
    <div className="w-full max-w-5xl">
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="mb-3 text-xs font-black uppercase tracking-[0.24em] text-primary/70">Experience Content</p>
          <h1 className="text-3xl font-bold text-on-surface sm:text-4xl">
            {isWedding ? 'Build the Wedding Story' : isBirthday ? 'Build the Birthday Story' : 'Build the Event Story'}
          </h1>
          <p className="mt-3 max-w-2xl text-on-surface-variant">
            {isWedding
              ? 'Organize the invitation like a premium microsite: couple, family, moments, events, and RSVP details.'
              : isBirthday
                ? 'Craft a premium birthday microsite: hero, celebrants, gallery, party schedule, venue, and interactive features.'
                : 'Organize your event invitation with beautiful details, location, and RSVP.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 self-start md:self-auto px-4 py-2.5 rounded-2xl border border-primary/30 bg-primary/5 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors shadow-sm"
        >
          <Icons.Edit3 size={16} />
          Change Template
        </button>
      </div>

      {validationIssues.length > 0 && (
        <div
          id="details-validation-summary"
          className="mb-6 rounded-3xl border border-status-failed/30 bg-status-failed/10 p-5 text-status-failed"
        >
          <p className="font-bold">Please complete the required details before review.</p>
          <ul className="mt-3 space-y-1 text-sm">
            {validationIssues.map((issue, index) => (
              <li key={`${issue.panel}-${issue.message}-${index}`}>{issue.message}</li>
            ))}
          </ul>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        {!isBabyShower && (
          <>
        <EditorPanel id="overview" title={isWedding ? 'Wedding Overview' : 'Event Overview'} open={openPanels.includes('overview')} onToggle={() => togglePanel('overview')}>
          <div className="grid gap-5 md:grid-cols-2">
            {isWedding && (
              <>
                <Field
                  id="groomOverviewName"
                  label="Groom Name"
                  placeholder="Aarav"
                  value={wedding.groom?.fullName || wedding.groom?.nickname}
                  onChange={(val) => {
                    updatePerson('groom', { fullName: val, nickname: val });
                    const bName = wedding.bride?.fullName || wedding.bride?.nickname || 'Ananya';
                    if (val) updateData({ eventName: `${val} & ${bName}` });
                  }}
                  required
                />
                <Field
                  id="brideOverviewName"
                  label="Bride Name"
                  placeholder="Ananya"
                  value={wedding.bride?.fullName || wedding.bride?.nickname}
                  onChange={(val) => {
                    updatePerson('bride', { fullName: val, nickname: val });
                    const gName = wedding.groom?.fullName || wedding.groom?.nickname || 'Aarav';
                    if (val) updateData({ eventName: `${gName} & ${val}` });
                  }}
                  required
                />
              </>
            )}
            <Field id="eventName" label="Event Title" placeholder="Aarav & Ananya" value={data.eventName} onChange={(eventName) => { clearFieldIssue('eventName'); updateData({ eventName }); }} error={fieldError('eventName')} required />
            <Field id="hostName" label="Hosted By" placeholder="The Sharma & Mehta Families" value={data.hostName} onChange={(hostName) => { clearFieldIssue('hostName'); updateData({ hostName }); }} error={fieldError('hostName')} required />
            <Field id="_dateInput" label="Primary Date" type="date" min={todayInputValue()} value={data._dateInput} onChange={(_dateInput) => { validateDateInputChange(_dateInput, { panel: 'overview', field: '_dateInput', message: PAST_DATE_INPUT_MESSAGE }); updateData({ _dateInput }); }} error={fieldError('_dateInput')} required />
            <Field id="_timeInput" label="Primary Start Time" type="time" value={data._timeInput} onChange={(_timeInput) => { clearFieldIssue('_timeInput'); updateData({ _timeInput }); }} error={fieldError('_timeInput')} required />
            {isWedding && (
              <div className="flex flex-col gap-2">
                <label className={labelClass} htmlFor="targetLanguage">
                  Target Language / Script (Optional)
                </label>
                <select
                  id="targetLanguage"
                  className={inputClass}
                  value={wedding.targetLanguage || 'en'}
                  onChange={(e) => updateWedding({ targetLanguage: e.target.value as 'en' | 'mr' })}
                >
                  <option value="en">English (Default)</option>
                  <option value="mr">मराठी (Marathi - Phonetic Transliteration)</option>
                </select>
              </div>
            )}
            {data._dateInput === todayInputValue() && (
              <div className="md:col-span-2 rounded-2xl bg-amber-500/10 px-5 py-4 border border-amber-500/20 text-amber-600 dark:text-amber-400">
                <p className="flex items-start gap-3 text-sm font-semibold">
                  <Icons.Warning className="mt-0.5 shrink-0" size={18} />
                  <span>
                    Your event date is currently set to <strong>today</strong>. Please double-check and update it to your actual event date and time so guests get the correct information!
                  </span>
                </p>
              </div>
            )}
            <div className="md:col-span-2">
              <TextArea id="customMessage" label="Welcome Story" placeholder="A heartfelt note to guests..." value={data.customMessage} onChange={(customMessage) => updateData({ customMessage })} />
            </div>
          </div>
        </EditorPanel>

        <EditorPanel id="rsvp" title="Venue & RSVP" open={openPanels.includes('rsvp')} onToggle={() => togglePanel('rsvp')}>
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field id="eventVenue" label="Venue Name & Address" value={data.eventVenue} onChange={(eventVenue) => { clearFieldIssue('eventVenue'); updateData({ eventVenue }); }} error={fieldError('eventVenue')} required />
            </div>
            <Field id="rsvpLink" label="RSVP Link" type="url" placeholder="https://..." value={data.rsvpLink} onChange={(rsvpLink) => updateData({ rsvpLink })} />
          </div>
        </EditorPanel>
          </>
        )}

        {data.templateId === PLOT_PLANE_TEMPLATE_ID && (
          <EditorPanel
            id="plot-plane"
            title="Ceremony Photos"
            open={openPanels.includes('plot-plane')}
            onToggle={() => togglePanel('plot-plane')}
          >
            <PlotPlanePhotoSlots
              customData={data.customData}
              onSlotChange={(id, url) =>
                updateData((prev) => ({ customData: writePlotPlanePhoto(prev.customData, id, url) }))
              }
              invitationId={draftId}
              ensureInvitationId={saveDraftNow}
            />
          </EditorPanel>
        )}

        {isWedding && isTraditionalWeddingInteractive && (
          <EditorPanel id="sacred-verse" title="Sacred Verse Appearance" open={openPanels.includes('sacred-verse')} onToggle={() => togglePanel('sacred-verse')}>
            <div className="space-y-5">
              <TextArea
                id="sacred-verse-welcome"
                label="Family Welcome Message"
                placeholder="With the blessings of our elders and the grace of the Almighty, we warmly invite you and your family to join us in celebrating this joyous wedding occasion."
                value={wedding.sacredVerseWelcomeMessage}
                onChange={(sacredVerseWelcomeMessage) => updateWedding({ sacredVerseWelcomeMessage })}
                rows={3}
              />
              <MediaUploadField
                id="sacred-verse-god-image"
                label="Deity Image (optional)"
                accept="image"
                value={wedding.godImage}
                onChange={(godImage) => updateWedding({ godImage })}
                invitationId={draftId}
                ensureInvitationId={saveDraftNow}
                preprocess={removeImageBackground}
              />
              <p className="ml-1 text-xs text-on-surface-variant">
                The background is removed automatically so the artwork blends into the section. For best results,
                start with a clear photo where the deity stands out from its surroundings.
              </p>
              <div>
                <p className="text-sm leading-7 text-on-surface-variant mb-2">
                  By default this opening section uses a clean premium background. Optionally upload your own image
                  (mandap, temple, floral art, or anything else) to use instead.
                </p>
                <BgImageControls
                  sectionId="sacred-verse"
                  bg={wedding.sacredVerseBg}
                  onBgChange={(patch) => updateWedding({ sacredVerseBg: { ...(wedding.sacredVerseBg ?? {}), ...patch } })}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>

              <div className="border-t border-outline-variant/15 pt-5 space-y-4">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-shubh-vivah-image"
                    label="Use Custom शुभविवाह Artwork"
                    description="Upload your own designed शुभविवाह image instead of the built-in typography"
                    checked={Boolean(wedding.useShubhVivahImage)}
                    onChange={(v) => updateWedding({ useShubhVivahImage: v })}
                  />
                </div>
                {wedding.useShubhVivahImage && (
                  <MediaUploadField
                    id="shubh-vivah-image"
                    label="शुभविवाह Artwork"
                    accept="image"
                    value={wedding.shubhVivahImage}
                    onChange={(shubhVivahImage) => updateWedding({ shubhVivahImage })}
                    invitationId={draftId}
                    ensureInvitationId={saveDraftNow}
                  />
                )}
              </div>
            </div>
          </EditorPanel>
        )}

        {isWedding && data.templateId === 'nikah-elegant-experience' && (
          <EditorPanel id="nikah-details" title="Muslim Nikah Details" open={openPanels.includes('nikah-details')} onToggle={() => togglePanel('nikah-details')}>
            <div className="space-y-5">
              <Field
                id="nikah-bismillah"
                label="Bismillah Calligraphy Text"
                placeholder="e.g. بسم الله الرحمن الرحيم"
                value={wedding.nikahBismillah || ''}
                onChange={(v) => updateWedding({ nikahBismillah: v })}
              />
              <TextArea
                id="nikah-welcome"
                label="Sacred Union Welcome Message"
                placeholder="In the presence of family, friends and the Qazi, Ahmed and Ayesha will be joined in Nikah — a bond of love, mercy and tranquillity blessed by Allah."
                value={wedding.nikahWelcomeMessage || ''}
                onChange={(v) => updateWedding({ nikahWelcomeMessage: v })}
                rows={3}
              />
              <Field
                id="nikah-mehr"
                label="Mehr (Dowry / Pledge)"
                placeholder="A token of love and commitment, agreed with the blessings of both families."
                value={wedding.nikahMehr || ''}
                onChange={(v) => updateWedding({ nikahMehr: v })}
              />
              <Field
                id="nikah-qazi"
                label="Qazi / Officiant Name (optional)"
                placeholder="e.g. Qazi Sayyed Khalid"
                value={wedding.nikahQazi || ''}
                onChange={(v) => updateWedding({ nikahQazi: v })}
              />
              <div className="grid gap-5 md:grid-cols-2">
                <Field
                  id="nikah-custom-date"
                  label="Nikah Ceremony Date (optional)"
                  placeholder="e.g. Sunday, 7 December 2025"
                  value={wedding.nikahDate || ''}
                  onChange={(v) => updateWedding({ nikahDate: v })}
                />
                <Field
                  id="nikah-custom-time"
                  label="Nikah Ceremony Time (optional)"
                  placeholder="e.g. After Zuhr · 2:00 PM"
                  value={wedding.nikahTime || ''}
                  onChange={(v) => updateWedding({ nikahTime: v })}
                />
              </div>
              <Field
                id="nikah-custom-venue"
                label="Nikah Ceremony Venue (optional)"
                placeholder="e.g. The Emerald Banquet, Mumbai"
                value={wedding.nikahVenue || ''}
                onChange={(v) => updateWedding({ nikahVenue: v })}
              />
            </div>
          </EditorPanel>
        )}

        {isWedding && isTraditionalWeddingInteractive && (
          <EditorPanel id="couple-bg" title="Bride & Groom Section Appearance" open={openPanels.includes('couple-bg')} onToggle={() => togglePanel('couple-bg')}>
            <p className="text-sm leading-7 text-on-surface-variant mb-2">
              By default this section uses the template's theme background. Optionally upload your own image to use instead.
            </p>
            <BgImageControls
              sectionId="couple-bg"
              bg={wedding.coupleSectionBg}
              onBgChange={(patch) => updateWedding({ coupleSectionBg: { ...(wedding.coupleSectionBg ?? {}), ...patch } })}
              draftId={draftId}
              saveDraftNow={saveDraftNow}
            />
          </EditorPanel>
        )}

        {isWedding && (
          <>
            {(['groom', 'bride'] as const).map((side) => {
              const person = wedding[side] ?? {};
              const label = side === 'groom' ? 'Groom Details' : 'Bride Details';
              return (
                <EditorPanel key={side} id={side} title={label} open={openPanels.includes(side)} onToggle={() => togglePanel(side)}>
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <MediaUploadField id={`${side}-photoUrl`} label="Photo" accept="image" value={person.photoUrl} onChange={(photoUrl) => updatePerson(side, { photoUrl })} invitationId={draftId} ensureInvitationId={saveDraftNow} />
                    </div>
                    <div className="md:col-span-2">
                      <Field id={`${side}-fullName`} label="Full Name" value={person.fullName} onChange={(fullName) => { clearFieldIssue(`${side}-fullName`); updatePerson(side, { fullName }); }} error={fieldError(`${side}-fullName`)} />
                    </div>
                    <div className="md:col-span-2">
                      <Field id={`${side}-grandparentsNames`} label="Grandparents Names" placeholder="e.g. Late Shri Ramchandra & Smt. Savitribai" value={person.grandparentsNames} onChange={(grandparentsNames) => updatePerson(side, { grandparentsNames })} />
                    </div>
                    <Field id={`${side}-fatherName`} label="Father's Name" value={person.fatherName} onChange={(fatherName) => updatePerson(side, { fatherName })} />
                    <Field id={`${side}-motherName`} label="Mother's Name" value={person.motherName} onChange={(motherName) => updatePerson(side, { motherName })} />
                    <div className="md:col-span-2">
                      <Field id={`${side}-homeAddress`} label="Address" value={person.homeAddress} onChange={(homeAddress) => updatePerson(side, { homeAddress })} />
                    </div>
                    <div className="md:col-span-2">
                      <Field id={`${side}-familyPositionText`} label="Position Description" placeholder={side === 'groom' ? 'e.g. Second Son of Mr. & Mrs. Sharma' : 'e.g. Elder Daughter of Mr. & Mrs. Mehta'} value={person.familyPositionText} onChange={(familyPositionText) => updatePerson(side, { familyPositionText })} />
                    </div>
                    <Field id={`${side}-nickname`} label="Nickname" value={person.nickname} onChange={(nickname) => updatePerson(side, { nickname })} />
                    <Field id={`${side}-profession`} label="Profession" value={person.profession} onChange={(profession) => updatePerson(side, { profession })} />
                    <div className="md:col-span-2">
                      <TextArea id={`${side}-about`} label="About / Story" value={person.about} onChange={(about) => updatePerson(side, { about })} />
                    </div>
                  </div>
                </EditorPanel>
              );
            })}

            <EditorPanel id="events" title="Event Days / Schedule" open={openPanels.includes('events')} onToggle={() => togglePanel('events')}>
              <div className="space-y-6">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-wedding-schedule"
                    label="Show Wedding Schedule"
                    description="Display the Engagement/Haldi/Mehendi/Sangeet/Wedding/Reception timeline on the invitation"
                    checked={showWeddingSchedule}
                    onChange={(v) => updateWedding({ showWeddingSchedule: v })}
                  />
                </div>
                {showWeddingSchedule && (
                  <>
                    {events.map((event, index) => (
                      <div key={index} className="border-t border-outline-variant/20 pt-5 first:border-t-0 first:pt-0">
                        <div className="mb-4 flex items-center justify-between gap-4">
                          <h3 className="font-bold text-on-surface">Event {index + 1}</h3>
                          <div className="flex items-center gap-3">
                            <LiquidToggle
                              id={`event-visible-${index}`}
                              label="Show"
                              checked={event.visible !== false}
                              onChange={(visible) => updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, visible } : item))}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={`Remove event ${index + 1}`}
                              onClick={() => updateEvents(events.filter((_, itemIndex) => itemIndex !== index))}
                              disabled={events.length === 1}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                        <div className="grid gap-5 md:grid-cols-2">
                          <Field id={`event-title-${index}`} label="Title" value={event.title} onChange={(title) => { clearFieldIssue(`event-title-${index}`); updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, title } : item)); }} error={fieldError(`event-title-${index}`)} required={Boolean(fieldError(`event-title-${index}`))} />
                          <Field id={`event-date-${index}`} label="Date" type="date" min={todayInputValue()} value={event.date} onChange={(date) => { validateDateInputChange(date, { panel: 'events', field: `event-date-${index}`, message: `Event ${index + 1}: ${PAST_DATE_INPUT_MESSAGE}` }); updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, date } : item)); }} error={fieldError(`event-date-${index}`)} required={Boolean(fieldError(`event-date-${index}`))} />
                          <Field id={`event-start-${index}`} label="Start Time" value={event.startTime} placeholder="05:00 PM" onChange={(startTime) => updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, startTime } : item))} />
                          <Field id={`event-end-${index}`} label="End Time" value={event.endTime} placeholder="10:00 PM" onChange={(endTime) => updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, endTime } : item))} />
                          <Field id={`event-venue-${index}`} label="Venue" value={event.venue} onChange={(venue) => { clearFieldIssue(`event-venue-${index}`); updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, venue } : item)); }} error={fieldError(`event-venue-${index}`)} required={Boolean(fieldError(`event-venue-${index}`))} />
                          <MediaUploadField id={`event-cover-${index}`} label="Cover Image" accept="image" value={event.coverImageUrl} onChange={(coverImageUrl) => updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, coverImageUrl } : item))} invitationId={draftId} ensureInvitationId={saveDraftNow} />
                          <div className="md:col-span-2">
                            <TextArea id={`event-description-${index}`} label="Description" value={event.description} onChange={(description) => updateEvents(events.map((item, itemIndex) => itemIndex === index ? { ...item, description } : item))} rows={3} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => updateEvents([...events, emptyEvent()])}>Add Event</Button>
                  </>
                )}
              </div>
            </EditorPanel>

            {!isRoyalGanesh && (
              <>
                <EditorPanel id="family" title="Family Tree" open={openPanels.includes('family')} onToggle={() => togglePanel('family')}>
              <div className="space-y-5">

                {/* ── Groom's Family ── */}
                <div className="rounded-2xl border border-outline-variant/25 bg-surface-low p-5">
                  <LiquidToggle
                    id="toggle-groom-tree"
                    checked={showGroomFamilyTree}
                    onChange={(v) => updateWedding({ showGroomFamilyTree: v })}
                    label="Groom's Family"
                    description="Show groom's side couples on the invitation"
                  />
                  {showGroomFamilyTree && (
                    <div className="mt-4 space-y-3">
                      {groomFamilyCouples.length === 0 && (
                        <p className="text-xs text-on-surface-variant/50 italic">No family members yet — add one below.</p>
                      )}
                      {groomFamilyCouples.map((couple, i) => {
                        const isSingle = couple.type === 'single';
                        return (
                          <div key={couple.id} className="rounded-xl border border-outline-variant/20 bg-surface p-4 space-y-3">
                            {/* Type selector */}
                            <div className="flex items-center justify-between border-b border-outline-variant/10 pb-2">
                              <span className="text-xs font-semibold text-on-surface-variant">Type</span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                                    !isSingle
                                      ? 'bg-primary/10 border-primary text-primary'
                                      : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                  }`}
                                  onClick={() => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, type: 'couple' } : c))}
                                >
                                  Couple
                                </button>
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                                    isSingle
                                      ? 'bg-primary/10 border-primary text-primary'
                                      : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                  }`}
                                  onClick={() => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, type: 'single' } : c))}
                                >
                                  Single
                                </button>
                              </div>
                            </div>

                            {/* couple or single row */}
                            {isSingle ? (
                              <div className="flex flex-col gap-1 w-full">
                                <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-primary">Name</span>
                                <input
                                  className={inputClass}
                                  placeholder="e.g. Late Mr. Rajesh Sharma or Mrs. Sunita Sharma"
                                  value={couple.singleName ?? ''}
                                  onChange={(e) => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, singleName: e.target.value } : c))}
                                />
                              </div>
                            ) : (
                              <div className="flex items-end gap-2">
                                {/* MRS side */}
                                <div className="flex flex-1 flex-col gap-1">
                                  <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-rose-400">MRS.</span>
                                  <input
                                    className={inputClass}
                                    placeholder="Wife's name"
                                    value={couple.mrsName ?? ''}
                                    onChange={(e) => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, mrsName: e.target.value } : c))}
                                  />
                                </div>
                                {/* connector */}
                                <div className="flex shrink-0 flex-col items-center pb-3 text-on-surface-variant/30 select-none">
                                  <span className="text-xs leading-none">───</span>
                                  <span className="text-[10px] leading-none">♥</span>
                                  <span className="text-xs leading-none">───</span>
                                </div>
                                {/* MR side */}
                                <div className="flex flex-1 flex-col gap-1">
                                  <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-sky-400">MR.</span>
                                  <input
                                    className={inputClass}
                                    placeholder="Husband's name"
                                    value={couple.mrName ?? ''}
                                    onChange={(e) => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, mrName: e.target.value } : c))}
                                  />
                                </div>
                              </div>
                            )}

                            {/* label row */}
                            <div className="mt-3 flex items-center gap-3">
                              <input
                                className={`${inputClass} text-sm`}
                                placeholder="Label (e.g. Grandparents, Parents)"
                                value={couple.label ?? ''}
                                onChange={(e) => updateGroomFamilyCouples(groomFamilyCouples.map((c, idx) => idx === i ? { ...c, label: e.target.value } : c))}
                              />
                              <button
                                type="button"
                                aria-label="Remove couple"
                                onClick={() => updateGroomFamilyCouples(groomFamilyCouples.filter((_, idx) => idx !== i))}
                                className="shrink-0 rounded-xl border border-outline-variant/30 px-3 py-2 text-xs font-semibold text-on-surface-variant transition-colors hover:border-status-failed/40 hover:bg-status-failed/5 hover:text-status-failed"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => updateGroomFamilyCouples([...groomFamilyCouples, emptyFamilyCouple()])}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:border-primary/50 hover:text-primary"
                      >
                        <span className="text-lg leading-none">+</span> Add Family Member
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Bride's Family ── */}
                <div className="rounded-2xl border border-outline-variant/25 bg-surface-low p-5">
                  <LiquidToggle
                    id="toggle-bride-tree"
                    checked={showBrideFamilyTree}
                    onChange={(v) => updateWedding({ showBrideFamilyTree: v })}
                    label="Bride's Family"
                    description="Show bride's side couples on the invitation"
                  />
                  {showBrideFamilyTree && (
                    <div className="mt-4 space-y-3">
                      {brideFamilyCouples.length === 0 && (
                        <p className="text-xs text-on-surface-variant/50 italic">No family members yet — add one below.</p>
                      )}
                      {brideFamilyCouples.map((couple, i) => {
                        const isSingle = couple.type === 'single';
                        return (
                          <div key={couple.id} className="rounded-xl border border-outline-variant/20 bg-surface p-4 space-y-3">
                            {/* Type selector */}
                            <div className="flex items-center justify-between border-b border-outline-variant/10 pb-2">
                              <span className="text-xs font-semibold text-on-surface-variant">Type</span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                                    !isSingle
                                      ? 'bg-primary/10 border-primary text-primary'
                                      : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                  }`}
                                  onClick={() => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, type: 'couple' } : c))}
                                >
                                  Couple
                                </button>
                                <button
                                  type="button"
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                                    isSingle
                                      ? 'bg-primary/10 border-primary text-primary'
                                      : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                  }`}
                                  onClick={() => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, type: 'single' } : c))}
                                >
                                  Single
                                </button>
                              </div>
                            </div>

                            {/* couple or single row */}
                            {isSingle ? (
                              <div className="flex flex-col gap-1 w-full">
                                <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-primary">Name</span>
                                <input
                                  className={inputClass}
                                  placeholder="e.g. Late Mr. Rajesh Sharma or Mrs. Sunita Sharma"
                                  value={couple.singleName ?? ''}
                                  onChange={(e) => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, singleName: e.target.value } : c))}
                                />
                              </div>
                            ) : (
                              <div className="flex items-end gap-2">
                                {/* MRS side */}
                                <div className="flex flex-1 flex-col gap-1">
                                  <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-rose-400">MRS.</span>
                                  <input
                                    className={inputClass}
                                    placeholder="Wife's name"
                                    value={couple.mrsName ?? ''}
                                    onChange={(e) => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, mrsName: e.target.value } : c))}
                                  />
                                </div>
                                {/* connector */}
                                <div className="flex shrink-0 flex-col items-center pb-3 text-on-surface-variant/30 select-none">
                                  <span className="text-xs leading-none">───</span>
                                  <span className="text-[10px] leading-none">♥</span>
                                  <span className="text-xs leading-none">───</span>
                                </div>
                                {/* MR side */}
                                <div className="flex flex-1 flex-col gap-1">
                                  <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-sky-400">MR.</span>
                                  <input
                                    className={inputClass}
                                    placeholder="Husband's name"
                                    value={couple.mrName ?? ''}
                                    onChange={(e) => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, mrName: e.target.value } : c))}
                                  />
                                </div>
                              </div>
                            )}

                            {/* label row */}
                            <div className="mt-3 flex items-center gap-3">
                              <input
                                className={`${inputClass} text-sm`}
                                placeholder="Label (e.g. Grandparents, Parents)"
                                value={couple.label ?? ''}
                                onChange={(e) => updateBrideFamilyCouples(brideFamilyCouples.map((c, idx) => idx === i ? { ...c, label: e.target.value } : c))}
                              />
                              <button
                                type="button"
                                aria-label="Remove couple"
                                onClick={() => updateBrideFamilyCouples(brideFamilyCouples.filter((_, idx) => idx !== i))}
                                className="shrink-0 rounded-xl border border-outline-variant/30 px-3 py-2 text-xs font-semibold text-on-surface-variant transition-colors hover:border-status-failed/40 hover:bg-status-failed/5 hover:text-status-failed"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => updateBrideFamilyCouples([...brideFamilyCouples, emptyFamilyCouple()])}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:border-primary/50 hover:text-primary"
                      >
                        <span className="text-lg leading-none">+</span> Add Family Member
                      </button>
                    </div>
                  )}
                </div>

              </div>
            </EditorPanel>

            <EditorPanel id="invitations" title="Family Invitations" open={openPanels.includes('invitations')} onToggle={() => togglePanel('invitations')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-show-family-invitations"
                    label="Show Family Invitations"
                    description="Show cards from family members inviting guests on the invitation"
                    checked={showFamilyInvitations}
                    onChange={(v) => updateWedding({ showFamilyInvitations: v })}
                  />
                </div>
                {showFamilyInvitations && (
                  <>
                    <p className="text-sm leading-6 text-on-surface-variant">
                      Add family members who will appear in the "With Love From Our Family" section — each card shows their name, relation, message, and an optional photo.
                    </p>
                    {familyInvitations.length === 0 && (
                      <p className="text-xs text-on-surface-variant/50 italic">No cards yet — add one below.</p>
                    )}
                    {familyInvitations.map((card, index) => (
                      <div key={card.id} className="rounded-xl border border-outline-variant/20 bg-surface p-4 space-y-4">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-semibold text-on-surface">{card.name || `Card ${index + 1}`}</span>
                          <button
                            type="button"
                            aria-label="Remove family member"
                            onClick={() => updateFamilyInvitations(familyInvitations.filter((_, i) => i !== index))}
                            className="shrink-0 rounded-xl border border-outline-variant/30 px-3 py-1.5 text-xs font-semibold text-on-surface-variant transition-colors hover:border-status-failed/40 hover:bg-status-failed/5 hover:text-status-failed"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Field
                            id={`inv-name-${index}`}
                            label="Name"
                            placeholder="Uncle Ramesh"
                            value={card.name}
                            onChange={(name) => updateFamilyInvitations(familyInvitations.map((c, i) => i === index ? { ...c, name } : c))}
                          />
                          <Field
                            id={`inv-relation-${index}`}
                            label="Relation"
                            placeholder="Paternal Uncle"
                            value={card.relation}
                            onChange={(relation) => updateFamilyInvitations(familyInvitations.map((c, i) => i === index ? { ...c, relation } : c))}
                          />
                          <div className="md:col-span-2">
                            <TextArea
                              id={`inv-message-${index}`}
                              label="Message"
                              placeholder="Please join us and celebrate this special occasion with our family."
                              rows={2}
                              value={card.message}
                              onChange={(message) => updateFamilyInvitations(familyInvitations.map((c, i) => i === index ? { ...c, message } : c))}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <MediaUploadField
                              id={`inv-photo-${index}`}
                              label="Photo (optional)"
                              accept="image"
                              value={card.photoUrl}
                              onChange={(photoUrl) => updateFamilyInvitations(familyInvitations.map((c, i) => i === index ? { ...c, photoUrl } : c))}
                              invitationId={draftId}
                              ensureInvitationId={saveDraftNow}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateFamilyInvitations([...familyInvitations, emptyInvitationCard()])}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      <span className="text-lg leading-none">+</span> Add Family Member
                    </button>
                  </>
                )}
              </div>
            </EditorPanel>

            <EditorPanel id="organizers" title="Event Organizers" open={openPanels.includes('organizers')} onToggle={() => togglePanel('organizers')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-show-organizers"
                    label="Show Organizers"
                    description="Highlight the people coordinating and hosting the wedding"
                    checked={showOrganizers}
                    onChange={(v) => updateWedding({ showOrganizers: v })}
                  />
                </div>
                {showOrganizers && (
                  <>
                    {organizers.length === 0 && (
                      <p className="text-xs text-on-surface-variant/50 italic">No organizers yet — add one below.</p>
                    )}
                    {organizers.map((organizer, index) => (
                      <div key={organizer.id} className="rounded-xl border border-outline-variant/20 bg-surface p-4 space-y-4">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-semibold text-on-surface">{organizer.name || `Organizer ${index + 1}`}</span>
                          <button
                            type="button"
                            aria-label="Remove organizer"
                            onClick={() => updateOrganizers(organizers.filter((_, i) => i !== index))}
                            className="shrink-0 rounded-xl border border-outline-variant/30 px-3 py-1.5 text-xs font-semibold text-on-surface-variant transition-colors hover:border-status-failed/40 hover:bg-status-failed/5 hover:text-status-failed"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <Field
                            id={`org-name-${index}`}
                            label="Name"
                            placeholder="Mr. Rajesh Sharma"
                            value={organizer.name}
                            onChange={(name) => updateOrganizers(organizers.map((o, i) => i === index ? { ...o, name } : o))}
                          />
                          <Field
                            id={`org-designation-${index}`}
                            label="Designation (optional)"
                            placeholder="Main Organizer"
                            value={organizer.designation ?? ''}
                            onChange={(designation) => updateOrganizers(organizers.map((o, i) => i === index ? { ...o, designation } : o))}
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateOrganizers([...organizers, emptyOrganizer()])}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      <span className="text-lg leading-none">+</span> Add Organizer
                    </button>
                  </>
                )}
              </div>
            </EditorPanel>

            <EditorPanel id="family-children" title="Family Children (Little Stars)" open={openPanels.includes('family-children')} onToggle={() => togglePanel('family-children')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-show-children"
                    label="Show Family Children"
                    description="Show younger family members participating in the celebration"
                    checked={showFamilyChildren}
                    onChange={(v) => updateWedding({ showFamilyChildren: v })}
                  />
                </div>
                {showFamilyChildren && (
                  <>
                    <Field
                      id="family-children-title"
                      label="Section Heading"
                      placeholder="Our Little Stars"
                      value={familyChildrenTitle}
                      onChange={(title) => updateWedding({ familyChildrenTitle: title })}
                    />
                    {familyChildren.length === 0 && (
                      <p className="text-xs text-on-surface-variant/50 italic">No family children yet — add one below.</p>
                    )}
                    {familyChildren.map((child, index) => (
                      <div key={child.id} className="rounded-xl border border-outline-variant/20 bg-surface p-4 space-y-4">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-semibold text-on-surface">{child.name || `Child ${index + 1}`}</span>
                          <button
                            type="button"
                            aria-label="Remove child"
                            onClick={() => updateFamilyChildren(familyChildren.filter((_, i) => i !== index))}
                            className="shrink-0 rounded-xl border border-outline-variant/30 px-3 py-1.5 text-xs font-semibold text-on-surface-variant transition-colors hover:border-status-failed/40 hover:bg-status-failed/5 hover:text-status-failed"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <Field
                              id={`child-name-${index}`}
                              label="Name"
                              placeholder="Aryan"
                              value={child.name}
                              onChange={(name) => {
                                const guessed = guessGenderByName(name);
                                updateFamilyChildren(familyChildren.map((c, i) => i === index ? { ...c, name, gender: c.gender || guessed } : c));
                              }}
                            />
                          </div>
                          <div className="md:col-span-2 flex items-center justify-between border border-outline-variant/10 rounded-xl p-3 bg-surface-low">
                            <span className="text-sm font-semibold text-on-surface-variant">Gender</span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                                  child.gender === 'boy' || (!child.gender && guessGenderByName(child.name) === 'boy')
                                    ? 'bg-sky-500/10 border-sky-500 text-sky-600'
                                    : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                }`}
                                onClick={() => updateFamilyChildren(familyChildren.map((c, i) => i === index ? { ...c, gender: 'boy' } : c))}
                              >
                                Boy 👦
                              </button>
                              <button
                                type="button"
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                                  child.gender === 'girl' || (!child.gender && guessGenderByName(child.name) === 'girl')
                                    ? 'bg-rose-500/10 border-rose-500 text-rose-600'
                                    : 'border-outline-variant/30 text-on-surface-variant/80 hover:bg-surface-variant/10'
                                }`}
                                onClick={() => updateFamilyChildren(familyChildren.map((c, i) => i === index ? { ...c, gender: 'girl' } : c))}
                              >
                                Girl 👧
                              </button>
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <MediaUploadField
                              id={`child-photo-${index}`}
                              label="Photo (optional)"
                              accept="image"
                              value={child.photo}
                              onChange={(photo) => updateFamilyChildren(familyChildren.map((c, i) => i === index ? { ...c, photo } : c))}
                              invitationId={draftId}
                              ensureInvitationId={saveDraftNow}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateFamilyChildren([...familyChildren, emptyFamilyChild()])}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:border-primary/50 hover:text-primary"
                    >
                      <span className="text-lg leading-none">+</span> Add Child
                    </button>
                  </>
                )}
              </div>
            </EditorPanel>

            <EditorPanel id="story" title="Story & Moments" open={openPanels.includes('story')} onToggle={() => togglePanel('story')}>
              <div className="space-y-6">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-pre-wedding-story"
                    label="Add Story & Moments"
                    description="Include photos and moments leading up to the event"
                    checked={showPreWeddingStory}
                    onChange={(v) => updateWedding({ showPreWeddingStory: v })}
                  />
                </div>
                {showPreWeddingStory && (
                  <>
                    <Field id="pre-title" label="Story Title" value={wedding.preWeddingStory?.title} onChange={(title) => updateWedding({ preWeddingStory: { ...(wedding.preWeddingStory ?? {}), title } })} />
                    <TextArea id="pre-description" label="Story Description" value={wedding.preWeddingStory?.description} onChange={(description) => updateWedding({ preWeddingStory: { ...(wedding.preWeddingStory ?? {}), description } })} />
                    {preWeddingMoments.map((moment, index) => (
                      <div key={index} className="grid gap-4 border-t border-outline-variant/20 pt-5 md:grid-cols-2">
                        <MediaUploadField id={`pre-url-${index}`} label="Media" accept="both" value={moment.url} onChange={(url) => updatePreWeddingMoments(preWeddingMoments.map((item, itemIndex) => itemIndex === index ? { ...item, url } : item))} invitationId={draftId} ensureInvitationId={saveDraftNow} />
                        <div className="flex flex-col gap-2">
                          <label className={labelClass}>Type</label>
                          <select className={inputClass} value={moment.type} onChange={(e) => updatePreWeddingMoments(preWeddingMoments.map((item, itemIndex) => itemIndex === index ? { ...item, type: e.target.value as WeddingMediaItem['type'] } : item))}>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </div>
                        <Field id={`pre-caption-${index}`} label="Caption" value={moment.caption} onChange={(caption) => updatePreWeddingMoments(preWeddingMoments.map((item, itemIndex) => itemIndex === index ? { ...item, caption } : item))} />
                        <Field id={`pre-description-${index}`} label="Description" value={moment.description} onChange={(description) => updatePreWeddingMoments(preWeddingMoments.map((item, itemIndex) => itemIndex === index ? { ...item, description } : item))} />
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => updatePreWeddingMoments([...preWeddingMoments, emptyMedia('image')])}>Add Story Moment</Button>
                  </>
                )}
              </div>
            </EditorPanel>

            <EditorPanel id="media" title="Media Gallery" open={openPanels.includes('media')} onToggle={() => togglePanel('media')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-media-showcase"
                    label="Add Media Gallery"
                    description="Show a photo and video gallery section on the invitation"
                    checked={showMediaShowcase}
                    onChange={(v) => updateWedding({ showMediaShowcase: v })}
                  />
                </div>
                {showMediaShowcase && (
                  <>
                    <p className="text-sm leading-7 text-on-surface-variant">Paste image or video links, or upload files directly — uploads are stored on our CDN and the URL is filled in for you.</p>
                    {mediaShowcase.map((item, index) => (
                      <div key={index} className="grid gap-4 border-t border-outline-variant/20 pt-5 md:grid-cols-2">
                        <MediaUploadField id={`media-url-${index}`} label={item.type === 'video' ? 'Video' : 'Image'} accept={item.type} value={item.url} onChange={(url) => updateMediaShowcase(mediaShowcase.map((media, itemIndex) => itemIndex === index ? { ...media, url } : media))} invitationId={draftId} ensureInvitationId={saveDraftNow} />
                        <div className="flex flex-col gap-2">
                          <label className={labelClass}>Type</label>
                          <select className={inputClass} value={item.type} onChange={(e) => updateMediaShowcase(mediaShowcase.map((media, itemIndex) => itemIndex === index ? { ...media, type: e.target.value as WeddingMediaItem['type'] } : media))}>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </div>
                        <Field id={`media-caption-${index}`} label="Caption" value={item.caption} onChange={(caption) => updateMediaShowcase(mediaShowcase.map((media, itemIndex) => itemIndex === index ? { ...media, caption } : media))} />
                        <Field id={`media-description-${index}`} label="Description" value={item.description} onChange={(description) => updateMediaShowcase(mediaShowcase.map((media, itemIndex) => itemIndex === index ? { ...media, description } : media))} />
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => updateMediaShowcase([...mediaShowcase, emptyMedia('image')])}>Add Media</Button>
                  </>
                )}
              </div>
            </EditorPanel>

            <EditorPanel id="blessings" title="Blessings Wall" open={openPanels.includes('blessings')} onToggle={() => togglePanel('blessings')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="toggle-allow-blessings"
                    label="Enable Blessings Wall"
                    description="Allow guests to leave blessings and wishes for the couple"
                    checked={allowBlessings}
                    onChange={(v) => updateWedding({ allowBlessings: v })}
                  />
                </div>
                {allowBlessings && (
                  <div className="space-y-4 pt-2">
                    <Field
                      id="blessingsTitle"
                      label="Wall Title"
                      placeholder="Blessings For The Couple"
                      value={blessingsTitle}
                      onChange={(v) => updateWedding({ blessingsTitle: v })}
                    />
                    <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                      <LiquidToggle
                        id="toggle-require-approval"
                        label="Moderate Blessings"
                        description="Require host approval before messages appear on the wall"
                        checked={requireApproval}
                        onChange={(v) => updateWedding({ requireApproval: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                      <LiquidToggle
                        id="toggle-allow-photos"
                        label="Allow Photo Uploads"
                        description="Allow guests to attach a photo to their blessing"
                        checked={allowPhotos}
                        onChange={(v) => updateWedding({ allowPhotos: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                      <LiquidToggle
                        id="toggle-allow-reactions"
                        label="Allow Reactions"
                        description="Allow guests to react to blessings with love, blessings, and best wishes"
                        checked={allowReactions}
                        onChange={(v) => updateWedding({ allowReactions: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold text-on-surface">Loading Mode</label>
                        <span className="text-xs text-on-surface-variant/70 text-left">Choose how guests load older blessings</span>
                      </div>
                      <select
                        id="select-load-mode"
                        className="rounded-xl border border-outline-variant/30 bg-surface px-3 py-1.5 text-sm outline-none focus:border-primary dark:[color-scheme:dark]"
                        value={blessingsLoadMode}
                        onChange={(e) => updateWedding({ blessingsLoadMode: e.target.value as any })}
                      >
                        <option value="loadMore">Load More Button</option>
                        <option value="infiniteScroll">Infinite Scroll</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                      <LiquidToggle
                        id="toggle-mock-blessings"
                        label="Enable Mock Blessings (Dev Mode)"
                        description="Inject mock messages to test pagination, masonry, and performance"
                        checked={enableMockBlessings}
                        onChange={(v) => updateWedding({ enableMockBlessings: v })}
                      />
                    </div>
                    {enableMockBlessings && (
                      <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                        <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider text-left block">Mock Count</label>
                        <select
                          id="select-mock-count"
                          className="w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-2.5 text-sm outline-none focus:border-primary dark:[color-scheme:dark]"
                          value={mockBlessingsCount}
                          onChange={(e) => updateWedding({ mockBlessingsCount: Number(e.target.value) })}
                        >
                          <option value={10}>10 Blessings</option>
                          <option value={25}>25 Blessings</option>
                          <option value={50}>50 Blessings</option>
                          <option value={100}>100 Blessings</option>
                          <option value={200}>200 Blessings</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </EditorPanel>
              </>
            )}
          </>
        )}

        {/* ── Birthday-specific panels ─────────────────────────────────── */}
        {isBirthday && (
          <>
            {/* Birthday Person */}
            <EditorPanel id="birthday-person" title="Birthday Person" open={openPanels.includes('birthday-person')} onToggle={() => togglePanel('birthday-person')}>
              <div className="grid gap-5 md:grid-cols-2">
                <Field id="bd-name" label="Birthday Person Name" placeholder="Aarav" value={birthday.birthdayPersonName} onChange={(v) => updateBirthday({ birthdayPersonName: v })} />
                <Field id="bd-age" label="Age Turning" placeholder="7" value={birthday.ageTurning} onChange={(v) => updateBirthday({ ageTurning: v })} />
                <div className="md:col-span-2">
                  <Field id="bd-tagline" label="Celebration Tagline" placeholder="Celebrating a Day Filled with Joy, Laughter & Beautiful Memories" value={birthday.celebrationTagline} onChange={(v) => updateBirthday({ celebrationTagline: v })} />
                </div>
                <Field id="bd-time" label="Event Time" placeholder="6:00 PM" value={birthday.eventTime} onChange={(v) => updateBirthday({ eventTime: v })} />
                <Field id="bd-hosts" label="Host Names" placeholder="The Sharma Family" value={birthday.hostNames} onChange={(v) => updateBirthday({ hostNames: v })} />
                <div className="md:col-span-2">
                  <MediaUploadField
                    id="bd-hero-image"
                    label="Hero Background Photo"
                    accept="image"
                    value={birthday.heroBackgroundImageUrl}
                    onChange={(v) => updateBirthday({ heroBackgroundImageUrl: v })}
                    invitationId={draftId}
                    ensureInvitationId={saveDraftNow}
                  />
                </div>
              </div>
            </EditorPanel>

            {/* Snapshot Gallery */}
            <EditorPanel id="birthday-gallery" title="Snapshot Gallery" open={openPanels.includes('birthday-gallery')} onToggle={() => togglePanel('birthday-gallery')}>
              <div className="space-y-5">
                <p className="text-sm text-on-surface-variant leading-6">Upload up to 12 photos. Each can have an optional caption.</p>
                {(birthday.snapshots?.length ? birthday.snapshots : [{ imageUrl: '', caption: '' }] as BirthdaySnapshot[]).map((snap, index) => (
                  <div key={index} className="grid gap-4 md:grid-cols-2 border-t border-outline-variant/20 pt-5 first:border-t-0 first:pt-0">
                    <MediaUploadField
                      id={`bd-snap-img-${index}`}
                      label="Photo"
                      accept="image"
                      value={snap.imageUrl}
                      onChange={(imageUrl) => {
                        const updated = [...(birthday.snapshots ?? [{ imageUrl: '', caption: '' }])];
                        updated[index] = { ...updated[index], imageUrl };
                        updateBirthday({ snapshots: updated });
                      }}
                      invitationId={draftId}
                      ensureInvitationId={saveDraftNow}
                    />
                    <div className="flex flex-col gap-2">
                      <Field
                        id={`bd-snap-cap-${index}`}
                        label="Caption (optional)"
                        placeholder="First Smile"
                        value={snap.caption}
                        onChange={(caption) => {
                          const updated = [...(birthday.snapshots ?? [{ imageUrl: '', caption: '' }])];
                          updated[index] = { ...updated[index], caption };
                          updateBirthday({ snapshots: updated });
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start mt-auto"
                        onClick={() => {
                          const updated = (birthday.snapshots ?? []).filter((_, i) => i !== index);
                          updateBirthday({ snapshots: updated });
                        }}
                        disabled={(birthday.snapshots?.length ?? 0) <= 1}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                {(birthday.snapshots?.length ?? 0) < 12 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => updateBirthday({ snapshots: [...(birthday.snapshots ?? []), { imageUrl: '', caption: '' }] })}
                  >
                    Add Photo
                  </Button>
                )}
                <BgImageControls
                  sectionId="bd-gallery"
                  bg={birthday.sectionBackgrounds?.gallery}
                  onBgChange={(patch) => updateSectionBg('gallery', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Celebrants */}
            <EditorPanel id="birthday-celebrants" title="Celebrants" open={openPanels.includes('birthday-celebrants')} onToggle={() => togglePanel('birthday-celebrants')}>
              <div className="space-y-6">
                {(birthday.celebrants?.length ? birthday.celebrants : [{ name: '', relation: '' }] as BirthdayCelebrant[]).map((c, index) => (
                  <div key={index} className="border-t border-outline-variant/20 pt-5 first:border-t-0 first:pt-0">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field id={`bd-cel-name-${index}`} label="Name" placeholder="Birthday Star" value={c.name} onChange={(name) => {
                        const updated = [...(birthday.celebrants ?? [{ name: '', relation: '' }])];
                        updated[index] = { ...updated[index], name };
                        updateBirthday({ celebrants: updated });
                      }} />
                      <Field id={`bd-cel-rel-${index}`} label="Relation" placeholder="Mom / Dad / Brother" value={c.relation} onChange={(relation) => {
                        const updated = [...(birthday.celebrants ?? [])];
                        updated[index] = { ...updated[index], relation };
                        updateBirthday({ celebrants: updated });
                      }} />
                      <MediaUploadField
                        id={`bd-cel-img-${index}`}
                        label="Photo"
                        accept="image"
                        value={c.imageUrl}
                        onChange={(imageUrl) => {
                          const updated = [...(birthday.celebrants ?? [])];
                          updated[index] = { ...updated[index], imageUrl };
                          updateBirthday({ celebrants: updated });
                        }}
                        invitationId={draftId}
                        ensureInvitationId={saveDraftNow}
                      />
                      <div className="flex flex-col gap-2">
                        <label className={labelClass}>Portrait Style</label>
                        <select
                          className={inputClass}
                          value={c.portraitStyle ?? 'original'}
                          onChange={(e) => {
                            const updated = [...(birthday.celebrants ?? [])];
                            updated[index] = { ...updated[index], portraitStyle: e.target.value as BirthdayCelebrant['portraitStyle'] };
                            updateBirthday({ celebrants: updated });
                          }}
                        >
                          <option value="original">Original Photo</option>
                          <option value="ghibli">🎨 Ghibli Style (AI — coming soon)</option>
                          <option value="pixar">🎬 Pixar Style (AI — coming soon)</option>
                          <option value="cartoon">🖌️ Cartoon Style (AI — coming soon)</option>
                          <option value="watercolor">💧 Watercolor Style (AI — coming soon)</option>
                        </select>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => updateBirthday({ celebrants: (birthday.celebrants ?? []).filter((_, i) => i !== index) })}
                      disabled={(birthday.celebrants?.length ?? 0) <= 1}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => updateBirthday({ celebrants: [...(birthday.celebrants ?? []), { name: '', relation: '' }] })}
                >
                  Add Celebrant
                </Button>
                <BgImageControls
                  sectionId="bd-celebrants"
                  bg={birthday.sectionBackgrounds?.celebrants}
                  onBgChange={(patch) => updateSectionBg('celebrants', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Activity Schedule */}
            <EditorPanel id="birthday-schedule" title="Party Schedule" open={openPanels.includes('birthday-schedule')} onToggle={() => togglePanel('birthday-schedule')}>
              <div className="space-y-5">
                {(birthday.activities?.length ? birthday.activities : [{ time: '', title: '' }] as BirthdayActivity[]).map((act, index) => (
                  <div key={index} className="grid gap-4 md:grid-cols-2 border-t border-outline-variant/20 pt-5 first:border-t-0 first:pt-0">
                    <Field id={`bd-act-time-${index}`} label="Time" placeholder="6:00 PM" value={act.time} onChange={(time) => {
                      const updated = [...(birthday.activities ?? [])];
                      updated[index] = { ...updated[index], time };
                      updateBirthday({ activities: updated });
                    }} />
                    <Field id={`bd-act-title-${index}`} label="Activity" placeholder="Cake Cutting" value={act.title} onChange={(title) => {
                      const updated = [...(birthday.activities ?? [])];
                      updated[index] = { ...updated[index], title };
                      updateBirthday({ activities: updated });
                    }} />
                    <Field id={`bd-act-emoji-${index}`} label="Emoji (optional)" placeholder="🎂" value={act.emoji} onChange={(emoji) => {
                      const updated = [...(birthday.activities ?? [])];
                      updated[index] = { ...updated[index], emoji };
                      updateBirthday({ activities: updated });
                    }} />
                    <div className="flex flex-col gap-2 self-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateBirthday({ activities: (birthday.activities ?? []).filter((_, i) => i !== index) })}
                        disabled={(birthday.activities?.length ?? 0) <= 1}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="md:col-span-2">
                      <TextArea id={`bd-act-desc-${index}`} label="Description (optional)" rows={2} value={act.description} onChange={(description) => {
                        const updated = [...(birthday.activities ?? [])];
                        updated[index] = { ...updated[index], description };
                        updateBirthday({ activities: updated });
                      }} />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => updateBirthday({ activities: [...(birthday.activities ?? []), { time: '', title: '' }] })}
                >
                  Add Activity
                </Button>
                <BgImageControls
                  sectionId="bd-schedule"
                  bg={birthday.sectionBackgrounds?.schedule}
                  onBgChange={(patch) => updateSectionBg('schedule', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Memory Timeline */}
            <EditorPanel id="birthday-timeline" title="Memory Timeline" open={openPanels.includes('birthday-timeline')} onToggle={() => togglePanel('birthday-timeline')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="bd-show-timeline"
                    label="Show Memory Timeline"
                    description="Hidden sections are removed from the published invitation"
                    checked={birthday.showMemoryTimeline !== false}
                    onChange={(v) => updateBirthday({ showMemoryTimeline: v })}
                  />
                </div>
                <p className="text-sm text-on-surface-variant leading-6">Add milestone moments — First Smile, First Steps, First Birthday, etc.</p>
                {(birthday.timelineItems ?? []).map((item, index) => (
                  <div key={index} className="grid gap-4 md:grid-cols-2 border-t border-outline-variant/20 pt-5 first:border-t-0 first:pt-0">
                    <Field id={`bd-tl-title-${index}`} label="Milestone Title" placeholder="First Smile" value={item.title} onChange={(title) => {
                      const updated = [...(birthday.timelineItems ?? [])];
                      updated[index] = { ...updated[index], title };
                      updateBirthday({ timelineItems: updated });
                    }} />
                    <MediaUploadField
                      id={`bd-tl-img-${index}`}
                      label="Photo (optional)"
                      accept="image"
                      value={item.imageUrl}
                      onChange={(imageUrl) => {
                        const updated = [...(birthday.timelineItems ?? [])];
                        updated[index] = { ...updated[index], imageUrl };
                        updateBirthday({ timelineItems: updated });
                      }}
                      invitationId={draftId}
                      ensureInvitationId={saveDraftNow}
                    />
                    <div className="md:col-span-2">
                      <TextArea id={`bd-tl-desc-${index}`} label="Description (optional)" rows={2} value={item.description} onChange={(description) => {
                        const updated = [...(birthday.timelineItems ?? [])];
                        updated[index] = { ...updated[index], description };
                        updateBirthday({ timelineItems: updated });
                      }} />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => updateBirthday({ timelineItems: (birthday.timelineItems ?? []).filter((_, i) => i !== index) })}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => updateBirthday({ timelineItems: [...(birthday.timelineItems ?? []), { title: '' }] as BirthdayTimelineItem[] })}
                >
                  Add Milestone
                </Button>
                <BgImageControls
                  sectionId="bd-timeline"
                  bg={birthday.sectionBackgrounds?.timeline}
                  onBgChange={(patch) => updateSectionBg('timeline', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Fun Facts */}
            <EditorPanel id="birthday-facts" title="Fun Facts" open={openPanels.includes('birthday-facts')} onToggle={() => togglePanel('birthday-facts')}>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="bd-show-facts"
                    label="Show Fun Facts"
                    description="Hidden sections are removed from the published invitation"
                    checked={birthday.showFunFacts !== false}
                    onChange={(v) => updateBirthday({ showFunFacts: v })}
                  />
                </div>
                <p className="text-sm text-on-surface-variant leading-6">Share fun facts about the birthday person — favourite food, hobby, etc.</p>
                {(birthday.funFacts ?? []).map((fact, index) => (
                  <div key={index} className="grid gap-3 md:grid-cols-[1fr_1fr_auto] border-t border-outline-variant/20 pt-4 first:border-t-0 first:pt-0">
                    <Field id={`bd-fact-label-${index}`} label="Label" placeholder="Favorite Food" value={fact.label} onChange={(label) => {
                      const updated = [...(birthday.funFacts ?? [])];
                      updated[index] = { ...updated[index], label };
                      updateBirthday({ funFacts: updated });
                    }} />
                    <Field id={`bd-fact-val-${index}`} label="Value" placeholder="🍕 Pizza" value={fact.value} onChange={(value) => {
                      const updated = [...(birthday.funFacts ?? [])];
                      updated[index] = { ...updated[index], value };
                      updateBirthday({ funFacts: updated });
                    }} />
                    <Button
                      type="button"
                      variant="outline"
                      className="self-end"
                      onClick={() => updateBirthday({ funFacts: (birthday.funFacts ?? []).filter((_, i) => i !== index) })}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => updateBirthday({ funFacts: [...(birthday.funFacts ?? []), { label: '', value: '' }] as BirthdayFunFact[] })}
                >
                  Add Fun Fact
                </Button>
                <BgImageControls
                  sectionId="bd-facts"
                  bg={birthday.sectionBackgrounds?.funFacts}
                  onBgChange={(patch) => updateSectionBg('funFacts', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Venue Details */}
            <EditorPanel id="birthday-venue" title="Venue Details" open={openPanels.includes('birthday-venue')} onToggle={() => togglePanel('birthday-venue')}>
              <div className="grid gap-5 md:grid-cols-2">
                <Field id="bd-venue-name" label="Venue Name" value={birthday.venueName} onChange={(v) => updateBirthday({ venueName: v })} />
                <div className="md:col-span-2">
                  <Field id="bd-venue-addr" label="Full Address" value={birthday.venueAddress} onChange={(v) => updateBirthday({ venueAddress: v })} />
                </div>
                <Field id="bd-venue-maps" label="Google Maps URL" type="url" placeholder="https://maps.google.com/..." value={birthday.mapsLink} onChange={(v) => updateBirthday({ mapsLink: v })} />
                <div className="md:col-span-2">
                  <TextArea id="bd-venue-parking" label="Parking Instructions (optional)" rows={2} value={birthday.parkingInstructions} onChange={(v) => updateBirthday({ parkingInstructions: v })} />
                </div>
                <div className="md:col-span-2">
                  <MediaUploadField
                    id="bd-venue-img"
                    label="Venue Photo (optional)"
                    accept="image"
                    value={birthday.venueImageUrl}
                    onChange={(v) => updateBirthday({ venueImageUrl: v })}
                    invitationId={draftId}
                    ensureInvitationId={saveDraftNow}
                  />
                </div>
                <div className="md:col-span-2">
                  <BgImageControls
                    sectionId="bd-venue"
                    bg={birthday.sectionBackgrounds?.venue}
                    onBgChange={(patch) => updateSectionBg('venue', patch)}
                    draftId={draftId}
                    saveDraftNow={saveDraftNow}
                  />
                </div>
              </div>
            </EditorPanel>

            {/* Birthday Story */}
            <EditorPanel id="birthday-story" title="Birthday Story" open={openPanels.includes('birthday-story')} onToggle={() => togglePanel('birthday-story')}>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="bd-show-story"
                    label="Show Birthday Story"
                    description="Hidden sections are removed from the published invitation"
                    checked={birthday.showBirthdayStory !== false}
                    onChange={(v) => updateBirthday({ showBirthdayStory: v })}
                  />
                </div>
                <p className="text-sm text-on-surface-variant leading-6">
                  Share a personal story about the birthday person — a milestone, a memory, or a heartfelt message.
                </p>
                <Field
                  id="bd-story-title"
                  label="Story Title"
                  placeholder="Our Little Miracle"
                  value={(birthday.birthdayStory as BirthdayStory | undefined)?.title}
                  onChange={(v) => updateBirthday({ birthdayStory: { ...(birthday.birthdayStory ?? {}), title: v } })}
                />
                <TextArea
                  id="bd-story-content"
                  label="Story Content"
                  rows={6}
                  placeholder="One year ago our lives changed forever when Aarav entered our world..."
                  value={(birthday.birthdayStory as BirthdayStory | undefined)?.content}
                  onChange={(v) => updateBirthday({ birthdayStory: { ...(birthday.birthdayStory ?? {}), content: v } })}
                />
                <Field
                  id="bd-story-quote"
                  label="Optional Quote"
                  placeholder="A child is a blessing beyond words"
                  value={(birthday.birthdayStory as BirthdayStory | undefined)?.quote}
                  onChange={(v) => updateBirthday({ birthdayStory: { ...(birthday.birthdayStory ?? {}), quote: v } })}
                />
                <Field
                  id="bd-story-author"
                  label="Quote Author (optional)"
                  placeholder="— The Sharma Family"
                  value={(birthday.birthdayStory as BirthdayStory | undefined)?.author}
                  onChange={(v) => updateBirthday({ birthdayStory: { ...(birthday.birthdayStory ?? {}), author: v } })}
                />
                <BgImageControls
                  sectionId="bd-story"
                  bg={birthday.sectionBackgrounds?.story}
                  onBgChange={(patch) => updateSectionBg('story', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>

            {/* Memory Capsule */}
            <EditorPanel id="birthday-capsule" title="Memory Capsule" open={openPanels.includes('birthday-capsule')} onToggle={() => togglePanel('birthday-capsule')}>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3">
                  <LiquidToggle
                    id="bd-show-capsule"
                    label="Show Memory Capsule"
                    description="Hidden sections are removed from the published invitation"
                    checked={birthday.showMemoryCapsule !== false}
                    onChange={(v) => updateBirthday({ showMemoryCapsule: v })}
                  />
                </div>
                <p className="text-sm text-on-surface-variant leading-6">
                  When enabled, guests can leave a short video or photo message for the birthday person.
                  Messages are collected live on the published invitation — no editor input required.
                </p>
                <BgImageControls
                  sectionId="bd-capsule"
                  bg={birthday.sectionBackgrounds?.capsule}
                  onBgChange={(patch) => updateSectionBg('capsule', patch)}
                  draftId={draftId}
                  saveDraftNow={saveDraftNow}
                />
              </div>
            </EditorPanel>
          </>
        )}

        {data.occasionType === 'BABY_SHOWER' && (
          <>
            <EditorPanel id="baby-shower-details" title="Baby Shower Details" open={openPanels.includes('baby-shower-details')} onToggle={() => togglePanel('baby-shower-details')}>
              <div className="grid gap-5 md:grid-cols-2">
                <Field id="baby-motherName" label="Mother's Name" placeholder="Emma Harrington" value={baby.motherName} onChange={(v) => updateBaby({ motherName: v })} required />
                <Field id="baby-hostedBy" label="Hosted By" placeholder="The Harrington Family" value={baby.hostedBy} onChange={(v) => updateBaby({ hostedBy: v })} required />
                <Field id="baby-eventDate" label="Event Date" type="date" min={todayInputValue()} value={baby.eventDate} onChange={(v) => { updateBaby({ eventDate: v }); if (!data._dateInput) updateData({ _dateInput: v }); }} required />
                <Field id="baby-eventTime" label="Event Time" type="time" value={baby.eventTime} onChange={(v) => { updateBaby({ eventTime: v }); if (!data._timeInput) updateData({ _timeInput: v }); }} required />
                <Field id="baby-venueName" label="Venue Name" placeholder="The Stardust Pavilion" value={baby.venueName} onChange={(v) => updateBaby({ venueName: v })} required />
                <Field id="baby-mapLink" label="Google Map Link" type="url" placeholder="https://maps.app.goo.gl/..." value={baby.mapLink} onChange={(v) => updateBaby({ mapLink: v })} />
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface px-4 py-3 mb-2">
                    <LiquidToggle
                      id="baby-showDressCode"
                      label="Include Dress Code"
                      description="Optional dress code details for your guests"
                      checked={baby.showDressCode !== false}
                      onChange={(v) => updateBaby({ showDressCode: v })}
                    />
                  </div>
                  {baby.showDressCode !== false && (
                    <Field id="baby-dressCode" label="Dress Code" placeholder="Champagne gold or warm white" value={baby.dressCode} onChange={(v) => updateBaby({ dressCode: v })} />
                  )}
                </div>
                <div className="md:col-span-2">
                  <TextArea id="baby-message" label="Special Message" placeholder="Your love and blessings are the greatest gifts we could receive..." value={baby.message} onChange={(v) => updateBaby({ message: v })} />
                </div>
              </div>
            </EditorPanel>
          </>
        )}

        <div className="flex items-center justify-between border-t border-outline-variant/10 pt-8">
          <Button variant="outline" type="button" onClick={onBack} className="flex items-center gap-2">
            <Icons.ArrowLeft size={18} /> Back
          </Button>
          <Button size="lg" type="submit" className="flex items-center gap-2 shadow-lg shadow-primary/20">
            Continue to Review
            <Icons.ArrowRight size={18} />
          </Button>
        </div>
      </form>

      {/* Live Preview is OUTSIDE the <form> to prevent nested <form> invalid HTML.
          Templates (e.g. Lagna Patrika) render their own <form> for RSVP. */}
      {data.templateId && (
        <section className="mt-5 overflow-hidden rounded-3xl border border-outline-variant/25 bg-surface-lowest shadow-sm">
          <div className="flex items-center justify-between gap-5 p-5 sm:p-6">
            <div>
              <h2 className="text-lg font-bold text-on-surface">Live Preview</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Rendered with your selected template — exactly as guests will see it.
              </p>
            </div>
          </div>
          <div className="h-[70vh] overflow-y-auto border-t border-outline-variant/15 bg-[#11130f]">
            <LivePreview templateId={data.templateId} previewData={previewData} />
          </div>
        </section>
      )}
    </div>
  );
}
